import type { TFunction } from "i18next";

/** MED-1622: localStorage key (and BroadcastChannel name) of the idle lock. */
export const PIN_LOCK_STORAGE_KEY = "medecine360-admin-pin-lock";

/** MED-1644: lock delay used when the session does not carry the user's own. */
export const DEFAULT_PIN_LOCK_MINUTES = 15;

/** MED-1644: lock delays the user can choose, in minutes. */
export const PIN_LOCK_MINUTES_OPTIONS = [5, 15, 30, 60] as const;

export const getErrorCode = (error: unknown): string | undefined => {
  const code = (error as { code?: unknown })?.code;
  return typeof code === "string" ? code : undefined;
};

/** `data.remainingAttempts` of a PIN_INVALID answer, when the BFF sent it. */
export const getRemainingAttempts = (error: unknown): number | undefined => {
  const data = (error as { data?: { remainingAttempts?: unknown } })?.data;
  const remaining = data?.remainingAttempts;
  return typeof remaining === "number" ? remaining : undefined;
};

/** What the lock screen says after a failed PIN call. */
export const pinErrorMessage = (t: TFunction, error: unknown): string => {
  switch (getErrorCode(error)) {
    case "PIN_INVALID": {
      const count = getRemainingAttempts(error);
      return count === undefined
        ? t("pinLock.errors.invalid")
        : t("pinLock.errors.invalidRemaining", { count });
    }
    case "PIN_ATTEMPTS_EXCEEDED":
      return t("pinLock.errors.attemptsExceeded");
    case "PIN_ALREADY_SET":
      return t("pinLock.errors.alreadySet");
    case "PIN_INVALID_FORMAT":
      return t("pinLock.errors.invalidFormat");
    case "PIN_RESET_OTP_INVALID":
      return t("pinLock.errors.otpInvalid");
    case "PIN_RESET_RATE_LIMITED":
      return t("pinLock.errors.rateLimited");
    default:
      return t("pinLock.errors.generic");
  }
};

/** Forget the persisted lock flag without telling the other tabs to unlock. */
export const clearPersistedPinLock = () => {
  try {
    window.localStorage.removeItem(PIN_LOCK_STORAGE_KEY);
  } catch {
    // Storage unavailable: nothing was persisted.
  }
};
